import type {
	ITokenService,
	IUserStorageService,
} from "@/application/ports/services";
import type { IMonitoring } from "@/application/ports/IMonitoring";
import type { User } from "@/domain/user";
import { getUserErrorMessage } from "@/application/errors/ApiClientError";
import { toSentryContext } from "@/application/services/authUtils";

/**
 * LoginWithTokensUseCase
 *
 * Handles the post-authentication flow when tokens are provided directly
 * (e.g. after invitation acceptance). The caller is responsible for supplying
 * an already-mapped domain User — this use case only stores the tokens and
 * user data, then records the Sentry user context.
 */
export class LoginWithTokensUseCase {
	private readonly tokenService: ITokenService;
	private readonly userStorageService: IUserStorageService;
	private readonly monitoring: IMonitoring;

	constructor(
		tokenService: ITokenService,
		userStorageService: IUserStorageService,
		monitoring: IMonitoring,
	) {
		this.tokenService = tokenService;
		this.userStorageService = userStorageService;
		this.monitoring = monitoring;
	}

	execute(
		accessToken: string,
		refreshToken: string,
		user: User,
	): { success: boolean; error?: string } {
		try {
			this.tokenService.setToken(accessToken);
			this.tokenService.setRefreshToken(refreshToken);
			this.userStorageService.setCurrentUser(user);
			this.monitoring.setUserContext(toSentryContext(user));
			return { success: true };
		} catch (error) {
			return {
				success: false,
				error: getUserErrorMessage(error, "Token login failed"),
			};
		}
	}
}
